import React, { useState, useEffect, useCallback, useRef } from "react";
import { Device, Devices, DeviceCommands, RebootCommand, IRebootCommand, Button } from "@app/shared";
import { useFetch, useModel } from "../../../../../hooks";
import { dismissToast, toastError, toastInfo, toastSuccess } from "../../../../../helpers";
import { EditDeviceDetails, Icons } from "../../../../shared";
import { useInterval } from "react-use";

type DeviceConfigurationStepProps = {
    deviceId: string;
    facilityId: string;
    facilityName?: string;
    createdShapes?: Array<any>;
    onNext: () => void;
    onBack?: () => void;
    className?: string;
};

export const DeviceConfigurationStep: React.FC<DeviceConfigurationStepProps> = ({ deviceId, facilityId, facilityName, createdShapes, onNext, onBack, className }) => {
    const [device, ,] = useFetch(() => Devices.getById(deviceId), [deviceId]);
    const [isSaving, setIsSaving] = useState(false);
    const [isRebooting, setIsRebooting] = useState(false);
    const [isConfigurationSaved, setIsConfigurationSaved] = useState(false);
    const [hasFormChanges, setHasFormChanges] = useState(false);
    const confirmToastId = useRef("");

    const [editDevice, deviceHelpers] = useModel(Device, device);

    const [deviceCommands, , deviceCommandHelper] = useFetch(() => (deviceId ? Devices.getCommands(deviceId, 50, true) : Promise.resolve(undefined)), [deviceId]);

    useInterval(() => {
        if (isConfigurationSaved && !isRebooting) {
            deviceCommandHelper.refreshData();
        }
    }, 5000);

    useEffect(() => {
        if (device) {
            deviceHelpers.replaceModel(device);
            setHasFormChanges(false);
        }
    }, [device]);

    useEffect(() => {
        if (deviceCommands && deviceCommands.length > 0) {
            const configCommands = deviceCommands.filter((cmd) => cmd.$type?.includes("DeviceConfigCommand") || cmd.description?.toLowerCase().includes("config"));

            const hasPending = configCommands.some((cmd) => cmd.dateCompleted == null);
            const allCompleted = configCommands.every((cmd) => cmd.dateCompleted != null);

            if (hasPending) {
                setIsConfigurationSaved(true);
                setHasFormChanges(false);
            } else if (allCompleted && configCommands.length > 0) {
                setIsConfigurationSaved(true);
                setHasFormChanges(false);
            }
        }
    }, [deviceCommands]);

    const areConfigCommandsCompleted = useCallback(() => {
        if (!deviceCommands || deviceCommands.length === 0) return true;

        const configCommands = deviceCommands.filter((cmd) => cmd.$type?.includes("DeviceConfigCommand") || cmd.description?.toLowerCase().includes("config"));

        return configCommands.every((cmd) => cmd.dateCompleted != null);
    }, [deviceCommands]);

    const hasPendingConfigCommands = useCallback(() => {
        if (!deviceCommands || deviceCommands.length === 0) return false;

        const configCommands = deviceCommands.filter((cmd) => cmd.$type?.includes("DeviceConfigCommand") || cmd.description?.toLowerCase().includes("config"));

        return configCommands.some((cmd) => cmd.dateCompleted == null);
    }, [deviceCommands]);

    const handleFormChange = useCallback(() => {
        if (hasPendingConfigCommands()) {
            toastInfo("Configuration In Progress", "Please wait for the current configuration commands to complete before making changes.", 3000);
            return;
        }

        if (isConfigurationSaved) {
            setIsConfigurationSaved(false);
            setHasFormChanges(true);
        }
    }, [isConfigurationSaved, hasPendingConfigCommands]);

    const handleSaveConfiguration = useCallback(async () => {
        if (!editDevice.id) {
            toastError("Device not loaded");
            return;
        }

        setIsSaving(true);
        try {
            await Devices.save(editDevice as Device);
            setIsConfigurationSaved(true);
            setHasFormChanges(false);
            toastSuccess("Configuration saved successfully!", "Device configuration has been saved. Waiting for commands to be applied...");

            deviceCommandHelper.refreshData();
        } catch (error) {
            toastError(`Failed to save device configuration: ${error}`);
        } finally {
            setIsSaving(false);
        }
    }, [editDevice, deviceCommandHelper]);

    const sendRebootCommand = useCallback(async () => {
        if (!deviceId) {
            toastError("Device ID not available");
            return;
        }

        setIsRebooting(true);
        try {
            const rebootCommand = new RebootCommand({
                deviceId: deviceId,
                description: "Reboot device after configuration update",
            } as IRebootCommand);

            await DeviceCommands.sendCommand(rebootCommand);
            toastSuccess("Reboot command sent successfully!", "The device will restart to apply the new configuration.");

            // Proceed to next step after successful reboot command
            onNext();
        } catch (error) {
            console.error("Failed to send reboot command:", error);
            toastError("Failed to send reboot command", error instanceof Error ? error.message : undefined);
        } finally {
            setIsRebooting(false);
        }
    }, [deviceId, onNext]);

    const showRebootConfirmation = useCallback(() => {
        if (confirmToastId.current) {
            dismissToast(confirmToastId.current);
            confirmToastId.current = "";
            return;
        }

        const configCommandsCompleted = areConfigCommandsCompleted();
        const pendingConfigCommands = hasPendingConfigCommands();

        if (hasFormChanges || !isConfigurationSaved) {
            confirmToastId.current = toastInfo(
                "Save Configuration",
                <>
                    <div className="flex flex-col gap-3">
                        <p className="text-sm">Are you ready to save the device configuration?</p>
                        <ul className="text-sm space-y-1 ml-4">
                            <li className="flex items-center gap-2">
                                <span className="text-brand-primary">•</span>
                                Save current configuration settings
                            </li>
                            <li className="flex items-center gap-2">
                                <span className="text-warmGray-400">•</span>
                                <span className="text-warmGray-400">Wait for commands to apply</span>
                            </li>
                            <li className="flex items-center gap-2">
                                <span className="text-warmGray-400">•</span>
                                <span className="text-warmGray-400">Reboot device (final step)</span>
                            </li>
                        </ul>
                        <p className="text-sm text-amber-800 bg-gradient-to-r from-amber-50 to-amber-100 p-3 rounded-xl border border-amber-300">
                            <strong>Note:</strong> After saving, you'll need to wait for the configuration commands to be applied to the device.
                        </p>
                        <div className="flex gap-2 justify-end">
                            <Button
                                onClick={() => {
                                    dismissToast(confirmToastId.current);
                                    confirmToastId.current = "";
                                }}
                                className="px-4 py-2 text-sm bg-white border border-warmGray-300 hover:border-warmGray-400 text-neutral-700 rounded-xl transition-all duration-200 hover:bg-warmGray-50"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={async () => {
                                    await handleSaveConfiguration();
                                    dismissToast(confirmToastId.current);
                                    confirmToastId.current = "";
                                }}
                                className="px-4 py-2 text-sm bg-brand-primary hover:bg-brand-primary-darkened text-white rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
                                disabled={isSaving}
                            >
                                {isSaving ? "Saving..." : "Save Configuration"}
                            </Button>
                        </div>
                    </div>
                </>,
                0
            );
        } else if (pendingConfigCommands && !configCommandsCompleted) {
            toastInfo("Waiting for Configuration", "Configuration commands are still being applied to the device. Please wait...", 3000);
        } else {
            confirmToastId.current = toastInfo(
                "Reboot Device",
                <>
                    <div className="flex flex-col gap-3">
                        <p className="text-sm">Configuration has been applied successfully. Ready to reboot the device?</p>
                        <ul className="text-sm space-y-1 ml-4">
                            <li className="flex items-center gap-2">
                                <span className="text-emerald-600">✓</span>
                                Configuration settings saved
                            </li>
                            <li className="flex items-center gap-2">
                                <span className="text-emerald-600">✓</span>
                                Commands applied to device
                            </li>
                            <li className="flex items-center gap-2">
                                <span className="text-brand-primary">•</span>
                                Reboot the device
                            </li>
                            <li className="flex items-center gap-2">
                                <span className="text-brand-primary">•</span>
                                Complete the device setup process
                            </li>
                        </ul>
                        <p className="text-sm text-amber-800 bg-gradient-to-r from-amber-50 to-amber-100 p-3 rounded-xl border border-amber-300">
                            <strong>Note:</strong> The device will restart and may be temporarily unavailable during the reboot process.
                        </p>
                        <div className="flex gap-2 justify-end">
                            <Button
                                onClick={() => {
                                    dismissToast(confirmToastId.current);
                                    confirmToastId.current = "";
                                }}
                                className="px-4 py-2 text-sm bg-white border border-warmGray-300 hover:border-warmGray-400 text-neutral-700 rounded-xl transition-all duration-200 hover:bg-warmGray-50"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={async () => {
                                    await sendRebootCommand();
                                    dismissToast(confirmToastId.current);
                                    confirmToastId.current = "";
                                }}
                                className="px-4 py-2 text-sm bg-brand-primary hover:bg-brand-primary-darkened text-white rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
                                disabled={isRebooting}
                            >
                                {isRebooting ? "Rebooting..." : "Reboot Device"}
                            </Button>
                        </div>
                    </div>
                </>,
                0
            );
        }
    }, [handleSaveConfiguration, sendRebootCommand, isSaving, isRebooting, isConfigurationSaved, hasFormChanges, areConfigCommandsCompleted, hasPendingConfigCommands]);

    const handleContinue = useCallback(async () => {
        showRebootConfirmation();
    }, [showRebootConfirmation]);

    const getButtonState = () => {
        if (hasFormChanges || !isConfigurationSaved) {
            return {
                text: "Save Configuration",
                icon: <Icons.ArrowRight className="w-4 h-4" />,
                disabled: isSaving || isRebooting,
            };
        }

        if (isConfigurationSaved && hasPendingConfigCommands()) {
            return {
                text: "Waiting for Commands...",
                icon: <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>,
                disabled: true,
            };
        }

        if (isConfigurationSaved && areConfigCommandsCompleted()) {
            return {
                text: "Reboot Device",
                icon: <Icons.ArrowRight className="w-4 h-4" />,
                disabled: isSaving || isRebooting,
            };
        }

        return {
            text: "Save Configuration",
            icon: <Icons.ArrowRight className="w-4 h-4" />,
            disabled: isSaving || isRebooting,
        };
    };

    if (!device) {
        return (
            <div className="flex justify-center items-center h-96">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mx-auto mb-4"></div>
                    <p className="text-neutral-600">Loading device configuration...</p>
                </div>
            </div>
        );
    }

    const buttonState = getButtonState();

    return (
        <div className={`max-w-5xl mx-auto px-4 ${className || ""}`}>
            <div className="bg-white rounded-2xl shadow-lg border border-warmGray-100 mb-8 overflow-hidden">
                <div className="px-8 py-6 border-b border-warmGray-100 bg-gradient-to-r from-warmGray-50 to-white">
                    <div className="flex items-center gap-4 mb-4">
                        <div className="w-16 h-16 bg-gradient-to-br from-brand-primary to-brand-primary-darkened rounded-2xl flex items-center justify-center shadow-lg">
                            <Icons.Settings className="w-8 h-8 text-white" />
                        </div>
                        <div>
                            <h2 className="text-3xl font-bold text-neutral-900">Device Configuration</h2>
                            <p className="text-neutral-600 mt-1">Configure your device settings and parameters</p>
                        </div>
                    </div>

                    <div className="flex flex-col gap-3">
                        <div className="flex items-center gap-3">
                            <div className="w-6 h-6 bg-emerald-500 rounded-lg flex items-center justify-center">
                                <Icons.Video className="w-3 h-3 text-white" />
                            </div>
                            <span className="text-neutral-700 font-medium">Device: {device.name}</span>
                        </div>

                        {isConfigurationSaved && hasPendingConfigCommands() && (
                            <div className="flex items-center gap-3 bg-gradient-to-r from-terracotta-50 to-warmGray-50 border border-brand-primary border-opacity-20 rounded-xl px-4 py-3">
                                <div className="w-6 h-6 bg-brand-primary rounded-lg flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-3 w-3 border-b border-white"></div>
                                </div>
                                <span className="text-neutral-700 text-sm font-medium">Applying configuration commands...</span>
                            </div>
                        )}

                        {isConfigurationSaved && areConfigCommandsCompleted() && (
                            <div className="flex items-center gap-3 bg-gradient-to-r from-emerald-50 to-emerald-100 border border-emerald-300 rounded-xl px-4 py-3">
                                <div className="w-6 h-6 bg-emerald-500 rounded-lg flex items-center justify-center">
                                    <Icons.Success className="w-3 h-3 text-white" />
                                </div>
                                <span className="text-emerald-800 text-sm font-medium">Configuration commands applied successfully</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-warmGray-100 mb-8 overflow-hidden">
                <div className="px-8 py-8 max-h-[700px] overflow-y-auto">
                    <EditDeviceDetails device={editDevice} helpers={deviceHelpers} onChange={handleFormChange} />
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-warmGray-100 overflow-hidden">
                <div className="px-8 py-6 flex justify-between items-center bg-gradient-to-r from-warmGray-50 to-white">
                    {onBack && (
                        <Button onClick={onBack} disabled={isSaving || isRebooting} className="flex items-center gap-3 px-6 py-3 bg-white border border-warmGray-300 hover:border-warmGray-400 text-neutral-700 font-semibold rounded-xl transition-all duration-200 hover:bg-warmGray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                            <Icons.ArrowBack className="w-4 h-4" />
                            Back to Shape Mapping
                        </Button>
                    )}

                    <Button onClick={handleContinue} disabled={buttonState.disabled} className="flex items-center gap-3 px-8 py-4 bg-brand-primary hover:bg-brand-primary-darkened text-white font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none">
                        {isSaving ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                Saving Configuration...
                            </>
                        ) : isRebooting ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                Rebooting Device...
                            </>
                        ) : (
                            <>
                                {buttonState.text}
                                {buttonState.icon}
                            </>
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
};
